import React, { useState } from "react";
import {
  Drawer, Toolbar, List, ListItemButton, ListItemIcon, ListItemText, Divider, Typography, Box,
  Select, MenuItem, IconButton, Tooltip, FormControl,
} from "@mui/material";
import { NavLink, useLocation, useNavigate, useMatch } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Ticket,
  ShieldCheck,
  Bell,
  FolderKanban,
  BarChart3,
  Webhook,
  Users,
  Kanban,
  ListTodo,
  GitBranch,
  Plus,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { projectService } from "../api/services/projectService";
import CreateProjectDialog from "./CreateProjectDialog";
import { resolveDefaultProject, rememberProject, projectViewPath, VIEWS } from "../utils/ticketProjectNav";
import logo from "../assets/LOGO.png";

const DRAWER_WIDTH = 260;

const TICKET_VIEWS = [
  { view: "board", label: "Board", icon: Kanban },
  { view: "list", label: "List", icon: Ticket },
  { view: "backlog", label: "Backlog", icon: ListTodo },
];

const NAV_SECTIONS = [
  {
    items: [{ label: "Dashboard", to: "/dashboard", icon: LayoutDashboard }],
  },
  {
    title: "Admin",
    items: [
      { label: "Employees", to: "/admin/employees", icon: Users, perm: "admin.users.manage" },
      { label: "Roles & Teams", to: "/admin/roles", icon: ShieldCheck, perm: "admin.roles.manage" },
      { label: "SLA Settings", to: "/admin/sla", icon: Bell, perm: "sla.configure" },
      { label: "Workflow", to: "/admin/workflow", icon: GitBranch, perm: "admin.categories.manage" },
      { label: "Webhooks", to: "/admin/webhooks", icon: Webhook, perm: "admin.categories.manage" },
    ],
  },
  {
    title: "Work",
    items: [
      { label: "Reports", to: "/reports", icon: BarChart3, perm: "report.view" },
      { label: "Notifications", to: "/notifications", icon: Bell },
    ],
  },
];

const PROJECTS_ITEM = { label: "Projects", to: "/projects", icon: FolderKanban, perm: "project.view" };

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { T, theme } = useTheme();
  const { hasPermission } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: projectService.list });

  // Captures both the project id and the current view (board/list/backlog/plans)
  // from a URL like /tickets/p/:projectId/:view or /tickets/p/:projectId/plans/:planId/board
  // -- the plan-board route's extra segments are covered by the trailing "/*".
  const ticketRouteMatch = useMatch("/tickets/p/:projectId/:view/*");
  const routeProjectId = ticketRouteMatch?.params?.projectId || null;
  const routeView = VIEWS.includes(ticketRouteMatch?.params?.view) ? ticketRouteMatch.params.view : "board";

  const fallbackProject = resolveDefaultProject(projects);
  const selectedProjectId = routeProjectId || (fallbackProject ? String(fallbackProject.id) : "");

  const handleProjectChange = (newId) => {
    rememberProject(newId);
    navigate(projectViewPath(newId, routeView));
  };

  const handleProjectCreated = (project) => {
    rememberProject(project.id);
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    navigate(projectViewPath(project.id, "board"));
  };

  // Only show what this person can actually use. Each item's `perm` is the
  // exact permission its backend endpoint requires, so the menu can't
  // advertise a screen that would just 403 on open. Items with no `perm`
  // (Dashboard, Notifications) are available to every authenticated ATS user.
  const visibleSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.perm || hasPermission(item.perm)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
      }}
    >
      <Toolbar sx={{ px: 3, py: 0, height:"52px" }}>
        <img
          src={logo}
          alt="Hirality ATS"
          style={{
            height: 138,
            objectFit: "contain",
            // The logo art is white; invert it to dark in light mode so it stays legible.
            filter: theme === "light" ? "brightness(0) saturate(100%)" : "none",
            transition: "filter 0.25s",
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.nextSibling.style.display = "block";
          }}
        />
        <Typography
          variant="h6"
          sx={{ display: "none", fontWeight: 800, letterSpacing: 0.5, color: T.nav_text_active }}
        >
          Hirality ATS
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: T.sidebar_border }} />
      <Box sx={{ overflowY: "auto", py: 1 }}>
        {/* Dashboard */}
        {visibleSections[0] && (
          <Box sx={{ mb: 1 }}>
            <List sx={{ px: 1.5 }}>
              {visibleSections[0].items.map((item) => (
                <NavItem key={item.to} item={item} T={T} location={location} />
              ))}
            </List>
          </Box>
        )}

        {/* Tickets -- project-scoped: pick a project, then Board/List/Backlog (Backlog holds Phases + unphased tickets) */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ px: 3, pt: 2, pb: 0.5, display: "block", color: T.dim, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
          >
            Tickets
          </Typography>
          <Box sx={{ px: 1.5, mb: 1, display: "flex", gap: 0.75, alignItems: "center" }}>
            <FormControl size="small" fullWidth disabled={!projects || projects.length === 0}>
              <Select
                value={selectedProjectId}
                displayEmpty
                onChange={(e) => handleProjectChange(e.target.value)}
                sx={{
                  fontSize: 13, fontWeight: 600, color: T.text,
                  backgroundColor: T.inp_bg,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: T.border },
                }}
              >
                {(!projects || projects.length === 0) && (
                  <MenuItem value="" disabled>No projects yet</MenuItem>
                )}
                {(projects || []).map((p) => (
                  <MenuItem key={p.id} value={String(p.id)} sx={{ fontSize: 13 }}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="New project">
              <IconButton
                size="small"
                onClick={() => setCreateOpen(true)}
                sx={{ border: `1px solid ${T.border}`, borderRadius: 1.5, color: T.nav_text_active, flexShrink: 0 }}
              >
                <Plus size={16} />
              </IconButton>
            </Tooltip>
          </Box>
          <List sx={{ px: 1.5 }}>
            {TICKET_VIEWS.map(({ view, label, icon: Icon }) => {
              const disabled = !selectedProjectId;
              const to = disabled ? undefined : projectViewPath(selectedProjectId, view);
              const active = Boolean(selectedProjectId) && routeProjectId === selectedProjectId && routeView === view;
              return (
                <ListItemButton
                  key={view}
                  disabled={disabled}
                  onClick={() => to && navigate(to)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    color: active ? T.nav_text_active : T.nav_text,
                    backgroundColor: active ? T.sidebar_active : "transparent",
                    borderLeft: active ? `3px solid ${T.cyan}` : "3px solid transparent",
                    "&:hover": { backgroundColor: active ? T.sidebar_active : T.sidebar_hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                    <Icon size={19} strokeWidth={2} />
                  </ListItemIcon>
                  <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 500 }} />
                </ListItemButton>
              );
            })}
            {hasPermission(PROJECTS_ITEM.perm) && (
              <NavItem item={PROJECTS_ITEM} T={T} location={location} />
            )}
          </List>
        </Box>

        {/* Everything else */}
        {visibleSections.slice(1).map((section, idx) => (
          <Box key={idx} sx={{ mb: 1 }}>
            {section.title && (
              <Typography
                variant="caption"
                sx={{ px: 3, pt: 2, pb: 0.5, display: "block", color: T.dim, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
              >
                {section.title}
              </Typography>
            )}
            <List sx={{ px: 1.5 }}>
              {section.items.map((item) => (
                <NavItem key={item.to} item={item} T={T} location={location} />
              ))}
            </List>
          </Box>
        ))}
      </Box>

      <CreateProjectDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleProjectCreated} />
    </Drawer>
  );
}

function NavItem({ item, T, location }) {
  const Icon = item.icon;
  const active = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
  return (
    <ListItemButton
      component={NavLink}
      to={item.to}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        color: active ? T.nav_text_active : T.nav_text,
        backgroundColor: active ? T.sidebar_active : "transparent",
        borderLeft: active ? `3px solid ${T.cyan}` : "3px solid transparent",
        "&:hover": { backgroundColor: active ? T.sidebar_active : T.sidebar_hover },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
        <Icon size={19} strokeWidth={2} />
      </ListItemIcon>
      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 500 }} />
    </ListItemButton>
  );
}

export { DRAWER_WIDTH };
